import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { Box, Paper, Stack, Typography, Divider, Table, TableHead, TableRow, TableCell, TableBody, Button, TextField, Backdrop, CircularProgress, Chip } from '@mui/material';
import ApproveRejectButtons from '../components/common/ApproveRejectButtons';
import apiService from '../services/api.service';
import { readChargeExpenseCodes, sapExpenseFor } from '../utils/poCharges';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import AddIcon from '@mui/icons-material/Add';

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const compact = s.match(/^(\d{8})$/);
  if (compact) return `${s.slice(6,8)}-${s.slice(4,6)}-${s.slice(0,4)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

const PODetail: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const dispatch = useDispatch();
  const user = useSelector((s: any) => s.auth.user);
  const [data, setData] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [workflow, setWorkflow] = useState<any | null>(null);
  const [comment, setComment] = useState<string>('');
  const [actionProcessing, setActionProcessing] = useState<'approve'|'reject'|'return'|null>(null);
  const processingRef = useRef<boolean>(false);
  const [moveProcessing, setMoveProcessing] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState<any[]>([]);
  const [docuSignStatus, setDocuSignStatus] = useState<any | null>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const refreshTimerRef = useRef<any>(null);
  const sseRef = useRef<EventSource | null>(null);

  const isAuthorizedApprover = (() => {
    const ids = Array.isArray((workflow as any)?.pendingApproverUserIds) ? (workflow as any).pendingApproverUserIds : [];
    const uid = Number((user as any)?.userid || 0);
    return !!uid && ids.includes(uid);
  })();

  useEffect(() => {
    const initial = (location.state as any)?.po;
    if (initial) {
      setData(initial);
    }
  }, [location.state]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        setLoadError('');
        const r = await apiService.getPO(Number(id));
        const d = (r as any)?.data || r || {};
        setData(d);
        setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
        try {
          const wfRes = await apiService.getPOWorkflowStatus(Number(id));
          const wf = (wfRes as any)?.data || wfRes || null;
          setWorkflow(wf);
        } catch {
          setWorkflow(null);
        }
        try {
          const apprHistRes = await apiService.getPOApprovalHistory(Number(id));
          const apprHist = (apprHistRes as any)?.data || apprHistRes || [];
          setApprovalHistory(Array.isArray(apprHist) ? apprHist : []);
        } catch {
          setApprovalHistory([]);
        }
        try {
          const dsRes = await apiService.getPODocuSignStatus(Number(id));
          setDocuSignStatus(dsRes?.data || null);
        } catch {
          setDocuSignStatus(null);
        }
      } catch (e: any) {
        setData(null);
        setWorkflow(null);
        setApprovalHistory([]);
        setDocuSignStatus(null);
        setAttachments([]);
        const msg = e?.response?.data?.message || e?.message || 'Failed to load PO details';
        setLoadError(String(msg));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, user]);

  const statusVal = String((data as any)?.status || '');
  useEffect(() => {
    if (!id) return;
    const pending = /pending/i.test(statusVal);
    if (pending && !refreshTimerRef.current) {
      refreshTimerRef.current = setInterval(async () => {
        try {
          const r = await apiService.getPO(Number(id));
          const d = (r as any)?.data || r || {};
          setData(d);
          try {
            const wfRes = await apiService.getPOWorkflowStatus(Number(id));
            const wf = (wfRes as any)?.data || wfRes || null;
            setWorkflow(wf);
            try {
              const apprHistRes = await apiService.getPOApprovalHistory(Number(id));
              const apprHist = (apprHistRes as any)?.data || apprHistRes || [];
              setApprovalHistory(Array.isArray(apprHist) ? apprHist : []);
            } catch {}
          } catch {}
        } catch {}
      }, 5000);
    }
    if (!pending && refreshTimerRef.current) {
      try { clearInterval(refreshTimerRef.current); } catch {}
      refreshTimerRef.current = null;
    }
    return () => {
      if (refreshTimerRef.current) {
        try { clearInterval(refreshTimerRef.current); } catch {}
        refreshTimerRef.current = null;
      }
    };
  }, [id, statusVal]);

  useEffect(() => {
    if (!id) return;
    try {
      const es = apiService.subscribePOStatus(Number(id));
      sseRef.current = es as EventSource;
      es.onmessage = async (e: MessageEvent) => {
        try {
          const payload = JSON.parse(String(e.data || '{}'));
          if (payload && typeof payload === 'object') {
            if (payload.po) setData((prev: any) => ({ ...(prev || {}), ...(payload.po || {}) }));
            if (payload.workflow) setWorkflow(payload.workflow);
          }
        } catch {}
      };
      es.onerror = () => {};
    } catch {}
    return () => {
      try {
        if (sseRef.current) sseRef.current.close();
      } catch {}
      sseRef.current = null;
    };
  }, [id]);

  const handleApprove = async () => {
    if (processingRef.current) return;
    if (!id) return;
    processingRef.current = true;
    setActionProcessing('approve');
    try {
      setLoading(true);
      await apiService.processPOApproval(Number(id), 'approve', comment);
      dispatch(showSnackbar({ message: 'PO approved successfully', severity: 'success' }));
      try { window.dispatchEvent(new CustomEvent('po-updated', { detail: { id: Number(id), status: 'Approved' } })); } catch {}
      const r = await apiService.getPO(Number(id));
      setData((r as any)?.data || r || {});
      setAttachments(Array.isArray(((r as any)?.data || r || {}).attachments) ? (((r as any)?.data || r || {}).attachments) : []);
      const wfRes = await apiService.getPOWorkflowStatus(Number(id));
      setWorkflow((wfRes as any)?.data || wfRes || null);
      const apprHistRes = await apiService.getPOApprovalHistory(Number(id));
      setApprovalHistory((apprHistRes as any)?.data || apprHistRes || []);
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || 'Approval failed', severity: 'error' }));
    } finally {
      setLoading(false);
      setActionProcessing(null);
      processingRef.current = false;
    }
  };

  const handleReject = async () => {
    if (processingRef.current) return;
    if (!id) return;
    processingRef.current = true;
    setActionProcessing('reject');
    try {
      setLoading(true);
      await apiService.processPOApproval(Number(id), 'reject', comment);
      dispatch(showSnackbar({ message: 'PO rejected', severity: 'success' }));
      try { window.dispatchEvent(new CustomEvent('po-updated', { detail: { id: Number(id), status: 'Rejected' } })); } catch {}
      const r = await apiService.getPO(Number(id));
      setData((r as any)?.data || r || {});
      setAttachments(Array.isArray(((r as any)?.data || r || {}).attachments) ? (((r as any)?.data || r || {}).attachments) : []);
      const wfRes = await apiService.getPOWorkflowStatus(Number(id));
      setWorkflow((wfRes as any)?.data || wfRes || null);
      const apprHistRes = await apiService.getPOApprovalHistory(Number(id));
      setApprovalHistory((apprHistRes as any)?.data || apprHistRes || []);
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || 'Reject failed', severity: 'error' }));
    } finally {
      setLoading(false);
      setActionProcessing(null);
      processingRef.current = false;
    }
  };

  const handleReturn = async () => {
    if (processingRef.current) return;
    if (!id) return;
    processingRef.current = true;
    setActionProcessing('return');
    try {
      setLoading(true);
      await apiService.processPOApproval(Number(id), 'return', comment);
      dispatch(showSnackbar({ message: 'PO returned to initiator', severity: 'success' }));
      try { window.dispatchEvent(new CustomEvent('po-updated', { detail: { id: Number(id), status: 'Returned' } })); } catch {}
      const r = await apiService.getPO(Number(id));
      setData((r as any)?.data || r || {});
      setAttachments(Array.isArray(((r as any)?.data || r || {}).attachments) ? (((r as any)?.data || r || {}).attachments) : []);
      const wfRes = await apiService.getPOWorkflowStatus(Number(id));
      setWorkflow((wfRes as any)?.data || wfRes || null);
      const apprHistRes = await apiService.getPOApprovalHistory(Number(id));
      setApprovalHistory((apprHistRes as any)?.data || apprHistRes || []);
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || 'Return failed', severity: 'error' }));
    } finally {
      setLoading(false);
      setActionProcessing(null);
      processingRef.current = false;
    }
  };

  const handleMoveToCompleted = async () => {
    if (!id) return;
    setMoveProcessing(true);
    try {
      setLoading(true);
      await apiService.movePOToCompleted(Number(id));
      dispatch(showSnackbar({ message: 'PO moved to Completed', severity: 'success' }));
      try { window.dispatchEvent(new CustomEvent('po-updated', { detail: { id: Number(id), inboxStatus: 'Completed' } })); } catch {}
      const r = await apiService.getPO(Number(id));
      setData((r as any)?.data || r || {});
      setAttachments(Array.isArray(((r as any)?.data || r || {}).attachments) ? (((r as any)?.data || r || {}).attachments) : []);
    } catch (e: any) {
      dispatch(showSnackbar({ message: e?.response?.data?.message || 'Move failed', severity: 'error' }));
    } finally {
      setLoading(false);
      setMoveProcessing(false);
    }
  };

  const goBack = () => {
    if (loading || actionProcessing) return;
    setNavigating(true);
    try {
      if (navType === 'PUSH') navigate(-1);
      else navigate('/dashboard');
    } finally {
      setTimeout(() => setNavigating(false), 600);
    }
  };

  const goDashboard = () => {
    if (loading || actionProcessing) return;
    setNavigating(true);
    try {
      navigate('/dashboard');
    } finally {
      setTimeout(() => setNavigating(false), 600);
    }
  };

  const getWorkflowSteps = () => {
    const baseRaw = Array.isArray((workflow as any)?.steps) ? (workflow as any).steps : [];
    const stepMap = new Map<number, any>();
    for (const step of baseRaw) {
      const stepNum = Number(step.stepNumber || 0);
      if (stepNum > 0 && !stepMap.has(stepNum)) stepMap.set(stepNum, step);
    }
    const base = Array.from(stepMap.values()).sort((a, b) => Number(a.stepNumber || 0) - Number(b.stepNumber || 0));
    if (base.length) return base;

    const type = String((data as any)?.requisitiontype || '').trim();
    const alloc = String((data as any)?.allocationtype || '').trim();
    const roles: string[] = [];
    if ((type === 'Inventory' || type === 'Service') && alloc === 'Project') {
      roles.push('ProjectManager', 'OperationsManager');
    } else if ((type === 'Inventory' || type === 'Service') && alloc === 'CostCenter') {
      roles.push('CostCenterManager');
    } else if (type === 'FixedAsset' && alloc === 'Project') {
      roles.push('ProjectManager', 'OperationsManager', 'FinanceManager', 'GeneralManager');
    } else if (type === 'FixedAsset' && alloc === 'CostCenter') {
      roles.push('CostCenterManager', 'FinanceManager', 'GeneralManager');
    }
    const cur = Number((data as any)?.currentstep || 0);
    return roles.map((role, idx) => ({
      stepNumber: idx + 1,
      approverRole: role,
      approverName: '',
      approverEmail: '',
      status: (idx + 1) === cur ? 'In Approval' : 'Pending',
      actionDate: null,
      comments: ''
    }));
  };

  const profitView = useMemo(() => {
    const lines = Array.isArray((data as any)?.lines) ? (data as any).lines : [];
    const lineBase = lines.reduce((sum: number, ln: any) => sum + Number(ln?.linetotal || (Number(ln?.quantity || 0) * Number(ln?.unitprice || 0)) || 0), 0);
    const additionalChargesRows = (() => {
      const preferredOrder = [
        'OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)',
        'FREIGHT CHARGES',
        'CUSTOMS CHARGES',
        'DELIVERY ORDER',
        'LOCAL CLEARANCE CHARGES'
      ];
      const orderIndex = (type: string) => {
        const idx = preferredOrder.findIndex((x) => x.toUpperCase() === String(type || '').toUpperCase());
        return idx < 0 ? 999 : idx;
      };
      const src = (data as any)?.additionalcharges;
      if (Array.isArray(src)) {
        return src
          .map((r: any) => ({ type: String(r?.type || '').trim(), amount: Number(r?.amount) || 0 }))
          .filter((r: any) => r.type.length > 0)
          .sort((a: any, b: any) => {
            const d = orderIndex(a.type) - orderIndex(b.type);
            if (d !== 0) return d;
            return String(a.type).localeCompare(String(b.type));
          });
      }
      const legacy = [
        { type: 'CUSTOMS CHARGES', amount: Number((data as any)?.customduty) || 0 },
        { type: 'FREIGHT CHARGES', amount: Number((data as any)?.freight) || 0 },
        { type: 'OTHERS ESTIMATED CHARGES (Finance, Insurance, Installation)', amount: Number((data as any)?.othercharges) || 0 },
      ]
        .filter((r) => r.amount !== 0)
        .sort((a: any, b: any) => orderIndex(a.type) - orderIndex(b.type));
      return legacy;
    })();
    const additionalChargesTotal = additionalChargesRows.reduce((sum: number, r: any) => sum + (Number(r.amount) || 0), 0);

    const estimatedCostRaw = Number(
      (data as any)?.totalestimatedpocost ??
      (data as any)?.totalestimatedcost ??
      (data as any)?.estimatedcost ??
      (data as any)?.grandtotal ??
      (data as any)?.totalamount ??
      (lineBase + additionalChargesTotal)
    ) || 0;
    const sellingRaw = Number(
      (data as any)?.sellingprice ??
      (data as any)?.sellingPrice ??
      0
    ) || 0;
    const selling = Math.max(0, sellingRaw);
    const estimatedCost = Math.max(0, estimatedCostRaw);
    const profitAmount = selling > 0 ? (selling - estimatedCost) : 0;
    const safeProfitAmount = Number.isFinite(profitAmount) ? profitAmount : 0;
    const profitPct = selling > 0 ? (safeProfitAmount / selling) * 100 : 0;
    const safePct = Number.isFinite(profitPct) ? profitPct : 0;

    const positiveProfit = Math.max(0, safeProfitAmount);
    const baseTotal = positiveProfit + estimatedCost;
    const costPctForPie = baseTotal > 0 ? (estimatedCost / baseTotal) * 100 : 100;
    const marginPctForPie = Math.max(0, 100 - costPctForPie);

    const currency = String((data as any)?.currency || 'SAR');
    const divisions = Array.from(new Set(lines.map((ln: any) => String(ln?.dim1 || '').trim()).filter(Boolean)));
    const departments = Array.from(new Set(lines.map((ln: any) => String(ln?.dim2 || '').trim()).filter(Boolean)));
    const supplierNames = Array.from(new Set(
      lines
        .map((ln: any) => String(ln?.vendorname || '').trim())
        .filter(Boolean)
    ));
    if (!supplierNames.length && String((data as any)?.vendorname || '').trim()) {
      supplierNames.push(String((data as any)?.vendorname || '').trim());
    }
    const projectProfitAsPerCosting = Number(
      (data as any)?.projectprofitaspercosting ??
      (data as any)?.projectprofitcosting ??
      safePct
    ) || 0;
    return {
      currency,
      estimatedCost,
      selling,
      profitAmount: safeProfitAmount,
      profitPct: safePct,
      costPctForPie,
      marginPctForPie,
      hasSelling: selling > 0,
      additionalChargesRows,
      additionalChargesTotal,
      // Header discount, PRPO's tax estimate, and which charges go to SAP as freight (utils/poCharges).
      headerDiscAmt: Number((data as any)?.headerdiscamt) || 0,
      taxAmt: Number((data as any)?.taxamt) || 0,
      chargeCodes: readChargeExpenseCodes((data as any)?.chargeexpensecodes),
      divisions,
      departments,
      supplierNames,
      projectProfitAsPerCosting
    };
  }, [data]);
  // Line discounts (Create PO's Disc % / Disc Amt): the Lines table shows them when the PO has any.
  const hasLineDiscounts = Array.isArray(data?.lines) && data.lines.some((ln: any) => Number(ln?.discamt) > 0);

  const formatMoney = (n: number, currency: string) => `${currency} ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportApprovalHistory = async () => {
    if (!id) return;
    try {
      const rows = (approvalHistory || []) as any[];
      const headers = ['StepNumber','ApproverName','ApproverEmail','ApproverRole','Action','ActionDate','Comments'];
      const lines = [headers.join(',')].concat(rows.map((h) => [h.stepnumber, h.approvername, h.approveremail, h.approverrole, h.action, h.actiondate, (String(h.comments||'').replace(/\r?\n/g,' '))].map(String).join(',')));
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO-${id}-ApprovalHistory.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      dispatch(showSnackbar({ message: 'Approval history exported', severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'Failed to export approval history', severity: 'error' }));
    }
  };

  const refreshAttachments = async () => {
    if (!id) return;
    try {
      const res = await apiService.getPOAttachments(Number(id));
      const data = (res as any)?.data || res || [];
      setAttachments(Array.isArray(data) ? data : []);
    } catch {
      setAttachments([]);
    }
  };

  const onUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id) return;
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await apiService.uploadPOAttachments(Number(id), Array.from(files));
      await refreshAttachments();
      dispatch(showSnackbar({ message: 'Attachment(s) uploaded', severity: 'success' }));
    } catch (err: any) {
      dispatch(showSnackbar({ message: err?.response?.data?.message || 'Upload failed', severity: 'error' }));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
    <Paper
      component="article"
      aria-labelledby="po-detail-heading"
      elevation={0}
      sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={{ xs: 1.5, md: 2 }}
        mb={2}
      >
        <Typography
          component="h1"
          id="po-detail-heading"
          variant="h5"
          sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
        >
          Purchase Order
        </Typography>
        <Stack
          direction="column"
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
          >
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/create-po')} disabled={loading}>New PO</Button>
            <Button size="small" variant="outlined" onClick={goBack} disabled={loading}>Back</Button>
            <Button size="small" variant="outlined" onClick={goDashboard} disabled={loading}>Dashboard</Button>
            {(() => {
            const isInitiator = Number((user as any)?.userid || 0) === Number((data as any)?.requestedbyuserid || 0);
            const isRejected = String((data as any)?.status || '').toLowerCase() === 'rejected';
            if (!(isInitiator && isRejected)) return null;
            return (
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={handleMoveToCompleted}
                disabled={loading || moveProcessing}
                startIcon={moveProcessing ? <CircularProgress size={16} /> : undefined}
              >
                {moveProcessing ? 'Moving…' : 'Move to Completed'}
              </Button>
            );
          })()}
          {(() => {
            const isActionable = !!data && /pending/i.test(String(data.status || ''));
            const hasDocuSign = !!docuSignStatus && docuSignStatus.status !== 'completed' && docuSignStatus.status !== 'declined' && docuSignStatus.status !== 'voided';
            const canAct = isActionable && isAuthorizedApprover && !hasDocuSign;
          return (
            <ApproveRejectButtons
              onReturn={handleReturn}
              onReject={handleReject}
              onApprove={handleApprove}
              rejectDisabled={!!actionProcessing || loading || !canAct}
              approveDisabled={!!actionProcessing || loading || !canAct}
              returnDisabled={!!actionProcessing || loading || !canAct}
            />
          );
        })()}
          {(() => {
            const hasDocuSign = !!docuSignStatus && docuSignStatus.status !== 'completed' && docuSignStatus.status !== 'declined' && docuSignStatus.status !== 'voided';
            if (hasDocuSign) {
              return <Typography variant="caption" color="primary">Approval is being handled via DocuSign</Typography>;
            }
            const showWarn = !!data && /pending/i.test(String(data.status || '')) && !isAuthorizedApprover;
            return showWarn ? (
              <Typography variant="caption" color="error">Unauthorized: You are not an approved reviewer for this stage</Typography>
            ) : null;
          })()}
          </Stack>
          <TextField
            size="small"
            label="Comment"
            placeholder="Add a note for approve/reject/revert"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={2}
            sx={{ width: { xs: '100%', md: 360 } }}
            disabled={!!actionProcessing || loading}
          />
        </Stack>
      </Stack>

      {!data ? (
        <Typography variant="body2">{loading ? 'Loading...' : (loadError || 'PO not found')}</Typography>
      ) : (
        <Box>
          <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Stack spacing={0.8} sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>PO No: {data.ponumber}</Typography>
                <Typography variant="body2">Type: {data.requisitiontype}</Typography>
                {(data as any).allocationDetails ? (
                  <Typography variant="body2" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.8 }}>
                    <span>{(data as any).allocationDetails.type === 'Project' ? 'Project' : 'Cost Center'}:</span>
                    <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 1, bgcolor: '#fff7cc', border: '1px solid #facc15', fontWeight: 700 }}>
                      {(data as any).allocationDetails.code}
                    </Box>
                    <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 1, bgcolor: '#eff6ff', border: '1px solid #93c5fd', fontWeight: 700 }}>
                      {(data as any).allocationDetails.name}
                    </Box>
                  </Typography>
                ) : (
                  <Typography variant="body2">Allocation: {data.allocationtype} / {data.allocationcode}</Typography>
                )}
                <Typography variant="body2">Needed By: {toDDMMYYYY((data as any).requireddate)}</Typography>
              </Stack>
              <Stack spacing={0.8} sx={{ flex: 1 }}>
                <Typography variant="body2">Status: <strong>{data.status}</strong></Typography>
                {((data as any).sappodocentry || (data as any).sappodocnum) ? (
                  <Typography variant="body2">SAP PO: DocNum {(data as any).sappodocnum || ''} / DocEntry {(data as any).sappodocentry || ''}</Typography>
                ) : (
                  <Typography variant="body2">SAP PO: Not posted yet</Typography>
                )}
                <Typography variant="body2">Total: <strong>{Number(data.totalamount || 0).toFixed(2)} {data.currency || 'SAR'}</strong></Typography>
                <Typography variant="body2">Selling Price: <strong>{Number((data as any).sellingprice || 0).toFixed(2)} {data.currency || 'SAR'}</strong></Typography>
                {(data as any).vendorcode || (data as any).vendorname ? (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                    <span>Vendor:</span>
                    <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 1, bgcolor: '#ecfdf5', border: '1px solid #86efac', fontWeight: 700 }}>
                      {(data as any).vendorcode}
                    </Box>
                    <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 1, bgcolor: '#ecfdf5', border: '1px solid #86efac', fontWeight: 700 }}>
                      {(data as any).vendorname}
                    </Box>
                  </Typography>
                ) : null}
                {(data as any).paymenttermcode || (data as any).paymenttermname ? (
                  <Typography variant="body2">
                    Payment Terms: <strong>{String((data as any).paymenttermcode || '')}{(data as any).paymenttermname ? ` - ${String((data as any).paymenttermname)}` : ''}</strong>
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
            {(data as any).remarks || data.remarks ? (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff7e6', border: '1px solid #ffd699', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#b45309', mb: 0.5 }}>
                  Initiator Remarks
                </Typography>
                <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
                  {(data as any).remarks || data.remarks}
                </Typography>
              </Box>
            ) : null}
            {((data as any).deliverytype || (data as any).incoterms || (data as any).modeofshipment || (data as any).requireddocuments || (data as any).consignee || (data as any).shipmentrequirements) ? (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, border: '1px solid #dbe2ea', bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                  SAP Footer Details
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
                  {(data as any).deliverytype ? <Typography variant="body2"><strong>Delivery:</strong> {(data as any).deliverytype}</Typography> : null}
                  {(data as any).incoterms ? <Typography variant="body2"><strong>Incoterms:</strong> {(data as any).incoterms}</Typography> : null}
                  {(data as any).modeofshipment ? <Typography variant="body2"><strong>Mode of Shipment:</strong> {(data as any).modeofshipment}</Typography> : null}
                  {(data as any).consignee ? <Typography variant="body2"><strong>Consignee:</strong> {(data as any).consignee}</Typography> : null}
                  {(data as any).requireddocuments ? <Typography variant="body2" sx={{ flexBasis: '100%', whiteSpace: 'pre-wrap' }}><strong>Required Documents:</strong> {(data as any).requireddocuments}</Typography> : null}
                  {(data as any).shipmentrequirements ? <Typography variant="body2" sx={{ flexBasis: '100%', whiteSpace: 'pre-wrap' }}><strong>Shipment Requirements:</strong> {(data as any).shipmentrequirements}</Typography> : null}
                </Stack>
              </Box>
            ) : null}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, color: '#1e293b', letterSpacing: 0.2 }}>
            Profit Margin
          </Typography>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
            }}
          >
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, border: '1px solid #dbe2ea', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#fff' }}>
                <Box sx={{ px: 1.2, py: 0.9, bgcolor: '#f1f5f9', borderBottom: '1px solid #dbe2ea' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3, color: '#0f172a' }}>COSTING DETAILS</Typography>
                </Box>
                <Stack divider={<Divider flexItem />}>
                  <Stack direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.8 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>PO Total Price</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{formatMoney(Number((data as any)?.totalamount || 0), profitView.currency)}</Typography>
                  </Stack>
                  {profitView.headerDiscAmt > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.7 }}>
                      <Typography sx={{ fontSize: 12.5, color: '#334155' }}>Header discount</Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{formatMoney(-profitView.headerDiscAmt, profitView.currency)}</Typography>
                    </Stack>
                  )}
                  {profitView.taxAmt > 0 && (
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.7 }}>
                      <Typography sx={{ fontSize: 12.5, color: '#334155' }}>Tax (estimate; SAP adds VAT itself)</Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{formatMoney(profitView.taxAmt, profitView.currency)}</Typography>
                    </Stack>
                  )}
                  {profitView.additionalChargesRows.map((r: any, idx: number) => (
                    <Stack key={`${r.type}-${idx}`} direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.7 }}>
                      <Typography sx={{ fontSize: 12.5, color: '#334155' }}>
                        {r.type}
                        {sapExpenseFor(profitView.chargeCodes, r.type) && (
                          <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: '#166534', fontWeight: 600 }}>SAP freight</Box>
                        )}
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{formatMoney(Number(r.amount) || 0, profitView.currency)}</Typography>
                    </Stack>
                  ))}
                  <Stack direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.8, bgcolor: '#f8fafc' }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Total Charges</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{formatMoney(profitView.additionalChargesTotal, profitView.currency)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.9, bgcolor: '#eef2ff' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, fontStyle: 'italic' }}>Total Estimated PO Cost</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{formatMoney(profitView.estimatedCost, profitView.currency)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ px: 1.2, py: 0.8 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Project Profit as per Costing</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{profitView.projectProfitAsPerCosting.toFixed(2)}%</Typography>
                  </Stack>
                  {(profitView.headerDiscAmt > 0 || profitView.taxAmt > 0 || profitView.additionalChargesTotal !== 0) && (
                    <Typography sx={{ px: 1.2, py: 0.8, fontSize: 11.5, color: '#64748b' }}>
                      SAP gets the PO total, the header discount and the charges marked SAP freight, and adds VAT itself. The tax and the other charges are PRPO estimates.
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Box sx={{ flex: 1, border: '1px solid #dbe2ea', borderRadius: 1.5, p: 1.2, bgcolor: '#fff' }}>
                <Box sx={{ px: 0.2, pb: 0.8, mb: 1, borderBottom: '1px solid #dbe2ea' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3, color: '#0f172a' }}>PROFIT DETAILS</Typography>
                </Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>Division: {profitView.divisions.length ? profitView.divisions.join(', ') : '-'}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>Department: {profitView.departments.length ? profitView.departments.join(', ') : '-'}</Typography>
                </Stack>
                {profitView.supplierNames.length > 0 && (
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', mb: 1 }}>
                    Supplier: {profitView.supplierNames.join(', ')}
                  </Typography>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  <Box sx={{ position: 'relative', width: 180, height: 180 }}>
                    <Box
                      sx={{
                        width: 180,
                        height: 180,
                        borderRadius: '50%',
                        background: `conic-gradient(#16a34a 0 ${profitView.marginPctForPie}%, #ef4444 ${profitView.marginPctForPie}% 100%)`,
                        boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.06), 0 8px 20px rgba(15,23,42,0.08)'
                      }}
                    />
                    <Box sx={{ position: 'absolute', inset: 22, borderRadius: '50%', bgcolor: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Profit Margin %</Typography>
                      <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1, color: profitView.profitAmount >= 0 ? '#065f46' : '#991b1b' }}>{profitView.profitPct.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                  <Stack spacing={0.8} sx={{ flex: 1, width: '100%' }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>PO Selling Price</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{formatMoney(profitView.selling, profitView.currency)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Profit Margin %</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: profitView.profitAmount >= 0 ? '#065f46' : '#991b1b' }}>{profitView.profitPct.toFixed(2)}%</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.4, pt: 0.8, borderTop: '1px solid #e2e8f0' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Profit Margin</Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 900, color: profitView.profitAmount >= 0 ? '#065f46' : '#991b1b', lineHeight: 1 }}>{formatMoney(profitView.profitAmount, profitView.currency)}</Typography>
                    </Stack>
                    {!profitView.hasSelling && (
                      <Typography variant="caption" sx={{ color: '#9a3412', mt: 0.5 }}>
                        Selling price is not available for this PO. Profit values default to 0.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>Workflow</Typography>
            <Button size="small" variant="outlined" onClick={exportApprovalHistory} disabled={loading}>Export Approval History</Button>
          </Stack>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assigned Approver(s)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Approved By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Decided At</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getWorkflowSteps().map((s: any, i: number) => {
                const isCurrent = Number(s.stepNumber) === Number((data as any)?.currentstep || 0);
                const hasAction = !!s.actionDate;
                let displayStatus = String(s.status || '');
                // A stage whose amount limit leaves this PO out comes as Skipped with a note.
                const notRequired = !hasAction && displayStatus === 'Skipped' && !!s.note;
                if (!hasAction) {
                  if (notRequired) displayStatus = 'Not required';
                  else if (isCurrent && displayStatus === 'Pending') displayStatus = 'In Approval';
                  else if (displayStatus === 'Skipped') displayStatus = 'Pending';
                }
                if (displayStatus === 'Returned') displayStatus = 'Revert Back';

                let actionedBy = '';
                const historyRows = (approvalHistory || []).filter((h: any) => Number(h.stepnumber) === Number(s.stepNumber));
                if (historyRows.length > 0) {
                  actionedBy = historyRows.map((h: any) => h.approvername).filter(Boolean).join(', ');
                } else if (s.approverName && !notRequired) {
                  actionedBy = s.approverName;
                }

                return (
                  <TableRow key={i}>
                    <TableCell>{s.stepNumber}</TableCell>
                    <TableCell>{s.approverRole}</TableCell>
                    <TableCell>{s.approverName || ''}</TableCell>
                    <TableCell>{actionedBy || ''}</TableCell>
                    <TableCell>{displayStatus}</TableCell>
                    <TableCell>{s.actionDate ? toDDMMYYYY(s.actionDate) : '-'}</TableCell>
                    <TableCell>
                      {(() => {
                        const rows = (approvalHistory || []).filter((h: any) => Number(h.stepnumber) === Number(s.stepNumber));
                        if (!rows.length) return s.comments || s.note || '';
                        return (
                          <Stack spacing={0.5}>
                            {rows.map((h: any, idx: number) => (
                              <Typography key={idx} variant="caption">
                                {`${toDDMMYYYY(h.actiondate)} • ${h.approvername || ''} • ${h.action === 'Returned' ? 'Revert Back' : h.action}${String(h.comments || '').trim() ? ' – ' + String(h.comments || '') : ''}`}
                              </Typography>
                            ))}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* DocuSign Status Section */}
          {docuSignStatus && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>
                DocuSign Signing Status
                <Chip
                  label={docuSignStatus.status?.toUpperCase()}
                  size="small"
                  color={docuSignStatus.status === 'completed' ? 'success' : docuSignStatus.status === 'declined' ? 'error' : 'primary'}
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Signer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Signed Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(docuSignStatus.signers || []).map((s: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{s.routingOrder}</TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={s.status}
                          size="small"
                          color={['completed', 'signed'].includes(s.status) ? 'success' : s.status === 'declined' ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{s.signedDate ? toDDMMYYYY(s.signedDate) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {docuSignStatus.status === 'completed' && data?.docusignlocalid && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => window.open(apiService.getDocuSignDownloadUrl(data.docusignlocalid), '_blank')}
                  sx={{ mb: 1 }}
                >
                  Download Signed Document
                </Button>
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>Attachments</Typography>
            <Stack direction="row" spacing={1}>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onFilesSelected} />
              <Button variant="contained" onClick={onUploadClick} disabled={uploading || loading}>Upload</Button>
              <Button variant="outlined" onClick={refreshAttachments} disabled={loading}>Refresh</Button>
            </Stack>
          </Stack>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(attachments || []).map((a: any, i: number) => {
                const aid = Number(a.attachmentid ?? a.AttachmentID ?? i);
                return (
                  <TableRow key={aid}>
                    <TableCell>{a.filename || a.FileName || ''}</TableCell>
                    <TableCell>{Number(a.filesize || a.FileSize || 0)}</TableCell>
                    <TableCell>{a.mimetype || a.MimeType || ''}</TableCell>
                    <TableCell>{a.uploadedbyname || a.UploadedByName || ''}</TableCell>
                    <TableCell>{a.uploadeddate || a.UploadedDate || ''}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" href={apiService.getPOAttachmentViewUrl(Number(id), aid)} target="_blank">View</Button>
                        <Button size="small" href={apiService.getPOAttachmentDownloadUrl(Number(id), aid)}>Download</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!attachments || !attachments.length) && (
                <TableRow>
                  <TableCell colSpan={6}>No attachments</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>Lines</Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Item/Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Source PR</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Warehouse</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>PR Open Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>PR Closed Qty</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>UoM</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                {hasLineDiscounts && <TableCell align="right" sx={{ fontWeight: 600 }}>Discount</TableCell>}
                <TableCell align="right" sx={{ fontWeight: 600 }}>Line Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Delivery Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Division</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Department</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Cost Code (WBS)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Auxiliary</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>LOC</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.lines || []).map((line: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>{line.linenum || idx + 1}</TableCell>
                  <TableCell>{line.itemcode || ''} {line.itemdescription || ''}</TableCell>
                  <TableCell>
                    {Number(line.sourceprid || 0) > 0 ? (
                      <Button size="small" variant="text" sx={{ minWidth: 0, p: 0 }} onClick={() => navigate(`/pr/${Number(line.sourceprid)}`)}>
                        {`PR #${Number(line.sourceprid)} / Line ${Number(line.sourceprlinenum || 0)}`}
                      </Button>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {line.vendorcode && line.vendorname
                      ? `${line.vendorcode} - ${line.vendorname}`
                      : (line.vendorcode || line.vendorname || '')}
                  </TableCell>
                  <TableCell>{line.warehouse || line.warehousecode || line.warehousename || ''}</TableCell>
                  <TableCell align="right">{Number(line.quantity || 0)}</TableCell>
                  <TableCell align="right">{Number(line.openqty || 0).toFixed(4)}</TableCell>
                  <TableCell align="right">{Number(line.closedqty || 0).toFixed(4)}</TableCell>
                  <TableCell align="right">{line.uomcode || ''}</TableCell>
                  <TableCell align="right">{Number(line.unitprice || 0).toFixed(2)}</TableCell>
                  {hasLineDiscounts && (
                    <TableCell align="right">
                      {Number(line.discamt) > 0 ? `${Number(Number(line.discpct || 0).toFixed(4))}% (${Number(line.discamt).toFixed(2)})` : ''}
                    </TableCell>
                  )}
                  <TableCell align="right">{Number(line.linetotal || (Number(line.quantity || 0) * Number(line.unitprice || 0))).toFixed(2)}</TableCell>
                  <TableCell align="right">{toDDMMYYYY(line.deliverydate)}</TableCell>
                  <TableCell align="right">{line.dim1 || ''}</TableCell>
                  <TableCell align="right">{line.dim2 || ''}</TableCell>
                  <TableCell align="right">{line.dim3 || ''}</TableCell>
                  <TableCell align="right">{line.dim4 || ''}</TableCell>
                  <TableCell align="right">{line.dim5 || ''}</TableCell>
                </TableRow>
              ))}
              {(!data.lines || !data.lines.length) && (
                <TableRow>
                  <TableCell colSpan={17}>No lines</TableCell>
                </TableRow>
              )}
              {(Array.isArray(data.lines) && data.lines.length > 0) && (
                <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 600 }}>
                  <TableCell colSpan={6} sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {data.lines.reduce((sum: number, ln: any) => sum + Number(ln.quantity || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell colSpan={hasLineDiscounts ? 4 : 3}></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {data.lines.reduce((sum: number, ln: any) => sum + Number(ln.linetotal || (Number(ln.quantity || 0) * Number(ln.unitprice || 0))), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell colSpan={6}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {(data as any).budgetInfo && (data as any).budgetInfo.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>Budget Information</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>WBS</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Allocated YTD</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Consumed YTD</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Available</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>This PO</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {((data as any).budgetInfo || []).map((b: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{b.departmentName || b.department || '-'}</TableCell>
                      <TableCell>{b.wbsName || b.wbs || '-'}</TableCell>
                      <TableCell>{Number(b.allocatedYTD || 0).toFixed(2)}</TableCell>
                      <TableCell>{Number(b.consumedYTD || 0).toFixed(2)}</TableCell>
                      <TableCell>{Number(b.availableWithTolerance || b.availableYTD || 0).toFixed(2)}</TableCell>
                      <TableCell>{Number(b.thisPOAmount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={b.status}
                          size="small"
                          color={
                            b.status === 'within_budget' ? 'success' :
                            b.status === 'within_tolerance' ? 'warning' :
                            b.status === 'exceeded' ? 'error' :
                            'default'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {(data as any).prRefs && (data as any).prRefs.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>PR Relations</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>PR</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Required Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reference Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {((data as any).prRefs || []).map((ref: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Button size="small" variant="text" onClick={() => navigate(`/pr/${ref.prid}`)}>
                          {String(ref.prnumber || '').trim() || `PR #${ref.prid}`}
                        </Button>
                      </TableCell>
                      <TableCell>{ref.prstatus || '-'}</TableCell>
                      <TableCell>{toDDMMYYYY(ref.requireddate)}</TableCell>
                      <TableCell>{Number(ref.prtotalamount || 0).toFixed(2)} {ref.prcurrency || (data as any)?.currency || ''}</TableCell>
                      <TableCell>{ref.reftype || '-'}</TableCell>
                      <TableCell>{toDDMMYYYY(ref.createdat)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Box>
      )}
    </Paper>
    <Backdrop open={loading} sx={{ zIndex: 9999 }}><CircularProgress /></Backdrop>
    <Backdrop open={!!actionProcessing} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress color="inherit" />
        <Typography variant="body1">
          {actionProcessing === 'approve' ? 'Processing approval. Please wait…' : actionProcessing === 'reject' ? 'Processing rejection. Please wait…' : 'Processing return. Please wait…'}
        </Typography>
        <Typography variant="body2">Do not close or click Approve/Reject until this finishes.</Typography>
      </Stack>
    </Backdrop>
    <Backdrop open={navigating} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 2 }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress color="inherit" />
        <Typography variant="body1">Navigating…</Typography>
      </Stack>
    </Backdrop>
    </>
  );
};

export default PODetail;
